import { useEffect, useState } from "react";
import { MapContainer, TileLayer, GeoJSON, LayersControl, useMap } from "react-leaflet";
import { Show } from "@refinedev/antd";
import { useParams, useNavigate, Link } from "react-router";
import { useTranslation } from "react-i18next";
import { Card, Spin, Button, Breadcrumb } from "antd";
import { ArrowLeftOutlined, LoadingOutlined, HomeOutlined, AuditOutlined, EnvironmentOutlined } from "@ant-design/icons";
import type { LatLngBoundsExpression, Layer } from "leaflet";
import L from "leaflet";
import type { FeatureCollection, Feature } from "geojson";
import "leaflet/dist/leaflet.css";
import { apiClient } from "../../services/api/apiClient";
import { usePageTitle } from "../../hooks";

// Fix for default marker icons in Leaflet
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png",
  iconUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png",
  shadowUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png",
});

// Map feature interface for audit map
interface AuditMapFeature {
  type: "Feature";
  geometry: {
    type: string;
    coordinates: number[][] | number[][][] | number[];
  };
  properties: {
    id?: number;
    name?: string;
    surface?: number;
    variety_name?: string;
    plot_type?: string;
    pattern?: string;
    kg_plot?: number;
    plants?: number;
    photo_count?: number;
    layer_type: "audit_plot" | "plot_visited" | "farm_plot" | "route";
    auditor?: string;
    date?: string;
    distance?: number;
    plot_name?: string;
  };
}

interface AuditMapData {
  type: "FeatureCollection";
  features: AuditMapFeature[];
}

// Component to handle map bounds adjustment
const MapBoundsHandler = ({ bounds }: { bounds: LatLngBoundsExpression | undefined }) => {
  const map = useMap();
  
  useEffect(() => {
    if (bounds) {
      // Fit bounds with padding
      map.fitBounds(bounds, { padding: [50, 50] });
    }
  }, [bounds, map]);
  
  return null;
};

export const AuditMap = () => {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [bounds, setBounds] = useState<LatLngBoundsExpression | undefined>(undefined);
  const [mapData, setMapData] = useState<AuditMapData | null>(null);
  const [isMapLoading, setIsMapLoading] = useState(true);
  const [auditReference, setAuditReference] = useState<string>("");
  
  // Set page title
  usePageTitle(auditReference ? `${t("audit.map_view", "Mapa")} - ${auditReference}` : t("audit.map_view", "Mapa de Auditoría"));

  // Fetch map data
  useEffect(() => {
    const fetchMapData = async () => {
      if (!id) return;
      
      try {
        setIsMapLoading(true);
        const url = `/audits/${id}/map_data/`;
        console.log("Fetching audit map data from:", url);
        
        const response = await apiClient.get<AuditMapData>(url);
        
        console.log("Audit map data received:", response.data);
        console.log("Total features:", response.data.features?.length || 0);
        
        setMapData(response.data);
      } catch (error) {
        console.error("Error fetching audit map data:", error);
      } finally {
        setIsMapLoading(false);
      }
    };

    fetchMapData();
  }, [id]);

  // Fetch audit reference for breadcrumb
  useEffect(() => {
    const fetchAuditReference = async () => {
      if (!id) return;
      
      try {
        const response = await apiClient.get(`/audits/${id}/`);
        setAuditReference(response.data.reference || id);
      } catch (error) {
        console.error("Error fetching audit reference:", error);
        setAuditReference(id);
      }
    };

    fetchAuditReference();
  }, [id]);

  // Calculate bounds when map data is loaded
  useEffect(() => {
    if (mapData?.features && mapData.features.length > 0) {
      const calculatedBounds = calculateBounds(mapData.features);
      console.log("Calculated bounds:", calculatedBounds);
      setBounds(calculatedBounds);
    }
  }, [mapData]);

  // Separate features by layer type
  const auditPlots = mapData?.features?.filter(
    (f: AuditMapFeature) => f.properties.layer_type === "audit_plot"
  ) || [];
  
  const plotsVisited = mapData?.features?.filter(
    (f: AuditMapFeature) => f.properties.layer_type === "plot_visited"
  ) || [];
  
  const farmPlots = mapData?.features?.filter(
    (f: AuditMapFeature) => f.properties.layer_type === "farm_plot"
  ) || [];
  
  const routes = mapData?.features?.filter(
    (f: AuditMapFeature) => f.properties.layer_type === "route"
  ) || [];
  
  // Debug logging
  console.log("Audit plots:", auditPlots.length);
  console.log("Plots visited:", plotsVisited.length);
  console.log("Farm plots:", farmPlots.length);
  console.log("Routes:", routes.length);

  // Default bounds if no features
  const defaultBounds: LatLngBoundsExpression = [
    [40.0, -4.0],
    [41.0, -3.0],
  ];

  // Custom breadcrumb
  const breadcrumb = (
    <Breadcrumb
      items={[
        {
          title: (
            <Link to="/">
              <HomeOutlined /> {t("home.title", "Inicio")}
            </Link>
          ),
        },
        {
          title: (
            <Link to="/audits">
              <AuditOutlined /> {t("audit.title", "Auditorías")}
            </Link>
          ),
        },
        {
          title: <Link to={`/audits/${id}`}>{auditReference || id}</Link>,
        },
        {
          title: (
            <>
              <EnvironmentOutlined /> {t("audit.map_view", "Vista de Mapa")}
            </>
          ),
        },
      ]}
    />
  );

  return (
    <Show
      isLoading={false}
      title={`${t("audit.map_view", "Mapa de Auditoría")}: ${auditReference || ""}`}
      breadcrumb={breadcrumb}
      headerButtons={() => (
        <Button
          type="default"
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate(`/audits/${id}`)}
        >
          {t("common.back", "Volver")}
        </Button>
      )}
    >
      <Card>
        {isMapLoading ? (
          <div style={{ 
            display: "flex", 
            justifyContent: "center", 
            alignItems: "center", 
            height: "600px",
            flexDirection: "column",
            gap: "16px"
          }}>
            <Spin 
              size="large" 
              indicator={<LoadingOutlined style={{ fontSize: 48 }} spin />}
            />
            <div style={{ color: "#666", fontSize: "16px" }}>
              {t("map.loading", "Cargando datos del mapa...")}
            </div>
          </div>
        ) : (
          <MapContainer
            bounds={bounds || defaultBounds}
            style={{ height: "600px", width: "100%" }}
            scrollWheelZoom={true}
            zoom={13}
          >
            <MapBoundsHandler bounds={bounds} />
            
            <LayersControl position="topright">
              {/* Satellite Base Layer */}
              <LayersControl.BaseLayer checked name={t("map.satellite", "Satélite")}>
                <TileLayer
                  url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
                  attribution="&copy; Esri"
                  maxZoom={19}
                />
              </LayersControl.BaseLayer>

              {/* Street Base Layer */}
              <LayersControl.BaseLayer name={t("map.streets", "Calles")}>
                <TileLayer
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  attribution="&copy; OpenStreetMap contributors"
                  maxZoom={19}
                />
              </LayersControl.BaseLayer>

              {/* Audit Plots Overlay Layer (Green - Parcelas Auditadas) */}
              {auditPlots.length > 0 && (
                <LayersControl.Overlay checked name={t("audit.audit_plots", "Parcelas Auditadas")}>
                  <GeoJSON
                    data={
                      {
                        type: "FeatureCollection",
                        features: auditPlots,
                      } as FeatureCollection
                    }
                    style={{
                      color: "#52c41a",
                      weight: 3,
                      fillOpacity: 0.4,
                    }}
                    onEachFeature={(feature: Feature, layer: Layer) => {
                      const props = feature.properties as any;
                      layer.bindPopup(`
                        <div style="font-family: Arial, sans-serif;">
                          <strong>${t("audit.plot_name", "Parcela")}:</strong> ${props.name || "N/A"}<br/>
                          <strong>${t("audit.surface", "Superficie")}:</strong> ${props.surface?.toFixed(2) || 0} ha<br/>
                          <strong>${t("audit.plot_type", "Tipo")}:</strong> ${props.plot_type || "N/A"}<br/>
                          <strong>${t("audit.pattern", "Patrón")}:</strong> ${props.pattern || "N/A"}<br/>
                          <strong>${t("audit.plants", "Plantas")}:</strong> ${props.plants || 0}<br/>
                          <strong>${t("audit.kg_plot", "Kg Parcela")}:</strong> ${props.kg_plot?.toFixed(0) || 0} kg<br/>
                          <strong>${t("audit.photos", "Fotos")}:</strong> ${props.photo_count || 0}
                        </div>
                      `);
                    }}
                  />
                </LayersControl.Overlay>
              )}

              {/* Plots Visited Overlay Layer (Green) */}
              {plotsVisited.length > 0 && (
                <LayersControl.Overlay name={t("audit.plots_visited", "Parcelas Visitadas")}>
                  <GeoJSON
                    data={
                      {
                        type: "FeatureCollection",
                        features: plotsVisited,
                      } as FeatureCollection
                    }
                    style={{
                      color: "#52c41a",
                      weight: 2,
                      fillOpacity: 0.3,
                    }}
                    onEachFeature={(feature: Feature, layer: Layer) => {
                      const props = feature.properties as any;
                      layer.bindPopup(`
                        <div style="font-family: Arial, sans-serif;">
                          <strong>${t("audit.plot_name", "Parcela")}:</strong> ${props.name || "N/A"}<br/>
                          <strong>${t("audit.surface", "Superficie")}:</strong> ${props.surface?.toFixed(2) || 0} ha<br/>
                          ${props.variety_name ? `<strong>${t("audit.variety", "Variedad")}:</strong> ${props.variety_name}<br/>` : ""}
                        </div>
                      `);
                    }}
                  />
                </LayersControl.Overlay>
              )}

              {/* Farm Plots Overlay Layer (Gray transparent) */}
              {farmPlots.length > 0 && (
                <LayersControl.Overlay checked name={t("audit.farm_plots", "Parcelas de la Finca")}>
                  <GeoJSON
                    data={
                      {
                        type: "FeatureCollection",
                        features: farmPlots,
                      } as FeatureCollection
                    }
                    style={{
                      color: "#8c8c8c",
                      weight: 1,
                      fillOpacity: 0.1,
                    }}
                    onEachFeature={(feature: Feature, layer: Layer) => {
                      const props = feature.properties as any;
                      layer.bindPopup(`
                        <div style="font-family: Arial, sans-serif;">
                          <strong>${t("audit.plot_name", "Parcela")}:</strong> ${props.name || props.plot_name || "N/A"}<br/>
                          <strong>${t("audit.surface", "Superficie")}:</strong> ${props.surface?.toFixed(2) || 0} ha
                        </div>
                      `);
                    }}
                  />
                </LayersControl.Overlay>
              )}

              {/* Auditor Routes Overlay Layer (Blue) */}
              {routes.length > 0 && (
                <LayersControl.Overlay checked name={t("audit.auditor_routes", "Rutas del Auditor")}>
                  <GeoJSON
                    data={
                      {
                        type: "FeatureCollection",
                        features: routes,
                      } as FeatureCollection
                    }
                    style={{
                      color: "#1890ff",
                      weight: 3,
                      opacity: 0.8,
                    }}
                    onEachFeature={(feature: Feature, layer: Layer) => {
                      const props = feature.properties as any;
                      layer.bindPopup(`
                        <div style="font-family: Arial, sans-serif;">
                          <strong>${t("audit.auditor", "Auditor")}:</strong> ${props.auditor || "N/A"}<br/>
                          <strong>${t("audit.date", "Fecha")}:</strong> ${props.date || "N/A"}<br/>
                          <strong>${t("audit.distance", "Distancia")}:</strong> ${props.distance?.toFixed(2) || 0} km
                        </div>
                      `);
                    }}
                  />
                </LayersControl.Overlay>
              )}
            </LayersControl>

            {/* Legend */}
            <div className="leaflet-bottom leaflet-left" style={{ marginBottom: "10px", marginLeft: "10px" }}>
              <div
                className="leaflet-control leaflet-bar"
                style={{ 
                  background: "white", 
                  padding: "10px",
                  borderRadius: "4px",
                  boxShadow: "0 1px 5px rgba(0,0,0,0.4)"
                }}
              >
                <div style={{ fontWeight: "bold", marginBottom: "8px", fontSize: "14px" }}>
                  {t("map.legend", "Leyenda")}
                </div>
                <div style={{ display: "flex", alignItems: "center", marginBottom: "4px" }}>
                  <span style={{ 
                    display: "inline-block",
                    width: "20px",
                    height: "3px",
                    backgroundColor: "#52c41a",
                    marginRight: "8px",
                    border: "2px solid #52c41a",
                    opacity: 0.6
                  }}></span>
                  <span style={{ fontSize: "12px" }}>{t("audit.audit_plots", "Parcelas Auditadas")}</span>
                </div>
                <div style={{ display: "flex", alignItems: "center", marginBottom: "4px" }}>
                  <span style={{ 
                    display: "inline-block",
                    width: "20px",
                    height: "3px",
                    backgroundColor: "#52c41a",
                    marginRight: "8px",
                    border: "1px solid #52c41a",
                    opacity: 0.3
                  }}></span>
                  <span style={{ fontSize: "12px" }}>{t("audit.plots_visited", "Parcelas Visitadas")}</span>
                </div>
                <div style={{ display: "flex", alignItems: "center", marginBottom: "4px" }}>
                  <span style={{ 
                    display: "inline-block",
                    width: "20px",
                    height: "3px",
                    backgroundColor: "#8c8c8c",
                    marginRight: "8px",
                    border: "1px solid #8c8c8c",
                    opacity: 0.5
                  }}></span>
                  <span style={{ fontSize: "12px" }}>{t("audit.farm_plots", "Parcelas de la Finca")}</span>
                </div>
                <div style={{ display: "flex", alignItems: "center" }}>
                  <span style={{ 
                    display: "inline-block",
                    width: "20px",
                    height: "3px",
                    backgroundColor: "#1890ff",
                    marginRight: "8px"
                  }}></span>
                  <span style={{ fontSize: "12px" }}>{t("audit.auditor_routes", "Rutas del Auditor")}</span>
                </div>
              </div>
            </div>
          </MapContainer>
        )}

        {!isMapLoading && mapData?.features?.length === 0 && (
          <div style={{ textAlign: "center", padding: "20px", color: "#999" }}>
            {t("map.no_data", "No hay datos disponibles para mostrar en el mapa")}
          </div>
        )}
      </Card>
    </Show>
  );
};

// Helper function to calculate bounds from all features
function calculateBounds(features: AuditMapFeature[]): LatLngBoundsExpression | undefined {
  if (!features || features.length === 0) {
    return undefined;
  }

  let minLat = Infinity;
  let maxLat = -Infinity;
  let minLng = Infinity;
  let maxLng = -Infinity;

  features.forEach((feature) => {
    if (feature.geometry && feature.geometry.coordinates) {
      const coords: any = feature.geometry.coordinates;
      const geomType = feature.geometry.type;
      
      // Handle Point geometry
      if (geomType === "Point" && Array.isArray(coords) && coords.length === 2 && typeof coords[0] === "number") {
        const lng = coords[0] as number;
        const lat = coords[1] as number;
        minLat = Math.min(minLat, lat);
        maxLat = Math.max(maxLat, lat);
        minLng = Math.min(minLng, lng);
        maxLng = Math.max(maxLng, lng);
      } 
      // Handle LineString geometry (routes)
      else if (geomType === "LineString" && Array.isArray(coords)) {
        coords.forEach((coord: any) => {
          if (Array.isArray(coord) && coord.length === 2 && typeof coord[0] === "number") {
            const lng = coord[0];
            const lat = coord[1];
            minLat = Math.min(minLat, lat);
            maxLat = Math.max(maxLat, lat);
            minLng = Math.min(minLng, lng);
            maxLng = Math.max(maxLng, lng);
          }
        });
      }
      // Handle Polygon and MultiPolygon
      else {
        const processCoordinates = (coordArray: any) => {
          if (Array.isArray(coordArray)) {
            coordArray.forEach((item: any) => {
              if (Array.isArray(item) && typeof item[0] === "number") {
                // This is a coordinate pair [lng, lat]
                const lng = item[0];
                const lat = item[1];
                minLat = Math.min(minLat, lat);
                maxLat = Math.max(maxLat, lat);
                minLng = Math.min(minLng, lng);
                maxLng = Math.max(maxLng, lng);
              } else {
                // Recurse for nested arrays
                processCoordinates(item);
              }
            });
          }
        };

        processCoordinates(coords);
      }
    }
  });

  if (minLat === Infinity || maxLat === -Infinity || minLng === Infinity || maxLng === -Infinity) {
    return undefined;
  }

  // Add a small padding to the bounds (about 5%)
  const latPadding = (maxLat - minLat) * 0.05;
  const lngPadding = (maxLng - minLng) * 0.05;

  return [
    [minLat - latPadding, minLng - lngPadding],
    [maxLat + latPadding, maxLng + lngPadding],
  ];
}
